import type { ReactNode } from 'react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, DialogBackdrop, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import clsx from 'clsx'
import { IconSvg } from 'components/common'

const FADE_HEIGHT_PX = 64
const FADE_GRADIENT = `linear-gradient(to bottom, black calc(100% - ${FADE_HEIGHT_PX}px), transparent)`

/** True while there is still content below the scrollport (within a 2px tolerance of the end). */
export function shouldShowFade(scrollTop: number, clientHeight: number, scrollHeight: number): boolean {
	return scrollTop + clientHeight < scrollHeight - 2
}

export interface ScrollableModalProps {
	/** Whether the modal is open. The parent owns this state. */
	open: boolean
	/** Called when the modal should close (Esc, backdrop click, or a button wired to it). */
	onClose: () => void
	/** Optional fixed header rendered above the scroll area — e.g. toolbar actions and a close button. Replaces the default title row. */
	header?: ReactNode
	/** Optional visible title, shown beside the default close button. When omitted, the dialog is named from `ariaLabel` instead. */
	title?: ReactNode
	/** Accessible name for the dialog (rendered visually hidden when there is no `title`). Required so the dialog is never unnamed. */
	ariaLabel: string
	/** Extra classes for the panel, e.g. width overrides like `sm:!max-w-3xl`. */
	className?: string
	/** Classes for the panel surface; passing one replaces the default translucent fill, e.g. `bg-primary-surface/[0.7]`. */
	surfaceClassName?: string
	/** Content rendered inside the scrollable body. */
	children: ReactNode
	/** Optional fixed footer below the scroll area — e.g. actions that must stay reachable while the body scrolls. */
	footer?: ReactNode
}

/**
 * Minimal modal shell: a fixed (optional) header above a scrollable body with the canonical
 * app scrollbar and a bottom fade that hints at more content until the user reaches the end,
 * plus an optional fixed footer for actions that must stay reachable.
 *
 * It intentionally supports far fewer props than `BaseModal` — add only what callers need.
 */
export function ScrollableModal({
	open,
	onClose,
	header,
	title,
	ariaLabel,
	className,
	surfaceClassName,
	children,
	footer,
}: Readonly<ScrollableModalProps>) {
	const bodyRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const [showFade, setShowFade] = useState(false)

	const updateFade = useCallback(() => {
		const el = bodyRef.current
		if (!el) return
		setShowFade(shouldShowFade(el.scrollTop, el.clientHeight, el.scrollHeight))
	}, [])

	useEffect(() => {
		if (!open) {
			setShowFade(false)
			return
		}
		updateFade()

		const el = bodyRef.current
		const content = contentRef.current
		if (!el || !content || typeof ResizeObserver === 'undefined') return

		// Content can grow after mount (e.g. an avatar image loads) without a scroll event, and a
		// viewport resize changes the max-height cap — both change whether the body overflows, so
		// re-measure the fade when either the container or its content resizes.
		const observer = new ResizeObserver(updateFade)
		observer.observe(el)
		observer.observe(content)
		return () => observer.disconnect()
	}, [open, updateFade])

	return (
		<Transition show={open} as={Fragment}>
			<Dialog as="div" className="fixed inset-0 overflow-y-auto z-10" onClose={onClose}>
				<div className="flex items-stretch justify-center h-[100dvh] min-h-[100dvh] p-0 text-center sm:items-end sm:block sm:h-auto sm:min-h-screen">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<DialogBackdrop className="fixed inset-0 bg-black-75 transition-opacity" onClick={onClose} />
					</TransitionChild>

					{/* This element is to trick the browser into centering the modal contents. */}
					<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
						&#8203;
					</span>

					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<div
							className={clsx(
								'relative block align-bottom overflow-hidden shadow-xl transform transition-all h-full w-full sm:inline-block sm:h-auto sm:align-middle sm:min-w-0 sm:max-w-lg',
								className
							)}
						>
							<div
								className={clsx(
									'relative flex h-full flex-col px-1 py-4 backdrop-blur-lg rounded-none sm:rounded-2xl sm:max-h-[calc(100dvh-6rem)] [@media(max-height:499px)]:!max-h-none',
									surfaceClassName ?? 'bg-primary-surface/[0.4]'
								)}
							>
								{header ? (
									<div className="shrink-0 p-4 pt-0">
										<DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
										{header}
									</div>
								) : (
									<div
										className={clsx(
											'shrink-0 flex items-center gap-4 p-4 pt-0',
											title ? 'justify-between' : 'justify-end'
										)}
									>
										<DialogTitle
											as="h3"
											className={
												title ? 'text-xl font-medium leading-6 text-primary-text' : 'sr-only'
											}
										>
											{title ?? ariaLabel}
										</DialogTitle>
										<button
											type="button"
											onClick={onClose}
											aria-label="Close"
											className="size-10 rounded-full flex justify-center items-center bg-white/10 shadow-button backdrop-blur-lg hover:bg-white-25"
										>
											<IconSvg icon="close" />
										</button>
									</div>
								)}
								<div
									ref={bodyRef}
									onScroll={updateFade}
									style={
										showFade
											? {
													maskImage: FADE_GRADIENT,
													WebkitMaskImage: FADE_GRADIENT,
												}
											: undefined
									}
									className="app-scrollbar--flush flex-1 min-h-0 overflow-y-auto sm:h-fit sm:max-h-[85vh] [@media(max-height:499px)]:!max-h-none"
								>
									<div ref={contentRef} className="px-4 py-8">{children}</div>
								</div>
								{footer && (
									<div className="shrink-0 px-4 pt-3 border-t border-translucent-plus-border">
										{footer}
									</div>
								)}
							</div>
						</div>
					</TransitionChild>
				</div>
			</Dialog>
		</Transition>
	)
}
